import type { ID, ISODateString, Rupees } from "./common";
import type { Order, OrderStatus } from "./order";
import type { TicketPriority } from "./support";

/** The online payment attempt behind an order (one per order). */
export const PAYMENT_RECORD_STATUSES = ["created", "paid", "failed", "refunded"] as const;
export type PaymentRecordStatus = (typeof PAYMENT_RECORD_STATUSES)[number];

/** What the browser needs to open Razorpay Checkout for an order. */
export interface CheckoutPayment {
  provider: "razorpay";
  /** Razorpay's public key id (safe to send to the browser). */
  keyId: string;
  razorpayOrderId: string;
  /** In paise, as Razorpay expects. */
  amount: number;
  currency: "INR";
  orderId: ID;
  orderNumber: string;
  customer: { name: string; phone: string; email: string | null };
}

/**
 * `POST /orders`: the order, plus the payment to collect when it was paid online. `payment` is
 * null for cash on delivery, orders the wallet fully covered, and demo mode.
 */
export interface PlaceOrderResult {
  order: Order;
  payment: CheckoutPayment | null;
}

/* Live admin events ---------------------------------------------------------- */

export const ADMIN_EVENT_TYPES = [
  "order.placed",
  "order.cancelled",
  "order.payment_failed",
] as const;
export type AdminEventType = (typeof ADMIN_EVENT_TYPES)[number];

export const ADMIN_TICKET_EVENT_TYPES = ["ticket.created"] as const;
export type AdminTicketEventType = (typeof ADMIN_TICKET_EVENT_TYPES)[number];

/** A customer raised a support ticket; pushed over `GET /admin/events` like order events. */
export interface AdminTicketEvent {
  type: AdminTicketEventType;
  ticketId: ID;
  ticketNumber: string;
  subject: string;
  customerName: string;
  priority: TicketPriority;
  at: ISODateString;
}

/** Pushed to open admin dashboards over `GET /admin/events` (Server-Sent Events). */
export interface AdminOrderEvent {
  type: AdminEventType;
  orderId: ID;
  orderNumber: string;
  customerName: string;
  total: Rupees;
  status: OrderStatus;
  area: string;
  at: ISODateString;
}
